import ArrowUp16 from '@carbon/icons-react/es/arrow--up/16';
import Movement16 from '@carbon/icons-react/es/movement/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { TaskIdV2 } from 'api/models';
import { ReactComponent as Milestone } from 'assets/svgs/milestone.svg';
import { ReactComponent as SaveTaskTemplateIcon } from 'assets/svgs/save-task-template.svg';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { ProjectTemplateTask } from 'data/templates/projects';
import {
  selectTemplateProjectPhase,
  selectTemplateTask,
} from 'data/templates/projects/selectors';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { RearrangeTasks } from '../../../commons';
import { useTemplateTaskCommonActions } from './actions';

const menu = ({
  onToggleTaskTemplate,
  parentEntity,
  task,
  deleteTask,
  onTogglePrivate,
  onToggleTaskType,
  onRearrange,
  onAddTaskAbove,
}) => (
  <Menu>
    <Menu.Header>
      <FormattedMessage id="more_actions" />
    </Menu.Header>
    <Menu.Item
      icon={<ArrowUp16 />}
      onClick={onAddTaskAbove}
      key="add_task_above"
    >
      <FormattedMessage id="templates.more_actions.add_task_above" />
    </Menu.Item>
    {!parentEntity.private && (
      <Menu.Item
        icon={task.private ? <View16 /> : <ViewOff16 />}
        onClick={onTogglePrivate}
        key="change_privacy"
      >
        <FormattedMessage
          id={
            task.private
              ? 'templates.more_actions.make_shared'
              : 'templates.more_actions.make_private'
          }
        />
      </Menu.Item>
    )}
    <Menu.Item
      icon={<Milestone />}
      key="change_type"
      onClick={onToggleTaskType}
    >
      <FormattedMessage
        id={
          task.type === 'MILESTONE'
            ? 'templates.more_actions.remove_milestone'
            : 'templates.more_actions.mark_milestone'
        }
      />
    </Menu.Item>
    <Menu.Item icon={<Movement16 />} key="move" onClick={onRearrange}>
      <FormattedMessage id="templates.more_actions.rearrange" />
    </Menu.Item>
    {parentEntity.tasks?.length > 0 && !task.formTemplateId && (
      <Menu.Item
        icon={<SaveTaskTemplateIcon />}
        key="save_task_template"
        onClick={onToggleTaskTemplate}
      >
        <FormattedMessage
          id={`templates.more_actions.${
            task.isTemplate ? 'remove_task_template' : 'save_task_template'
          }`}
        />
      </Menu.Item>
    )}
    <Menu.Divider />
    <Menu.ItemDelete key="delete" onClick={deleteTask} />
  </Menu>
);

const useParentEntity = (task: ProjectTemplateTask) => {
  const parentTask = useSelector(selectTemplateTask(task.parentTask?.taskId));
  const parentProjectPhase = useSelector(
    selectTemplateProjectPhase(task.projectPhase?.projectPhaseId),
  );

  return parentProjectPhase || parentTask;
};

export const TaskMoreActions: FC<{
  task: ProjectTemplateTask;
  hideHoverBorderRadius?: boolean;
}> = ({ task, hideHoverBorderRadius }) => {
  const parentEntity = useParentEntity(task);

  const {
    onDeleteTask,
    onTogglePrivate,
    onToggleTaskType,
    onAddTaskAbove,
    onToggleTaskTemplate,
  } = useTemplateTaskCommonActions(task);

  const deleteConfirmTitle = useTranslation(
    'templates.more_actions.delete.title',
  );
  const deleteConfirmText = useTranslation(
    'templates.more_actions.delete.task.text',
  );

  const deleteTask = useDeleteConfirmModal({
    title: deleteConfirmTitle,
    content: deleteConfirmText,
    onOk: onDeleteTask,
  });

  const [showRearrangeModal, setRerrangeModalVisibility] = useState(false);
  const taskId = useMemo(() => TaskIdV2(task.taskId), [task.taskId]);

  const onClose = useCallback(() => setRerrangeModalVisibility(false), []);

  const onRearrange = useCallback(() => {
    setRerrangeModalVisibility(true);
  }, []);

  const _menu = useMemo(
    () =>
      menu({
        parentEntity,
        task,
        deleteTask,
        onToggleTaskTemplate,
        onTogglePrivate,
        onToggleTaskType,
        onAddTaskAbove,
        onRearrange,
      }),
    [
      deleteTask,
      onAddTaskAbove,
      onRearrange,
      onTogglePrivate,
      onToggleTaskTemplate,
      onToggleTaskType,
      parentEntity,
      task,
    ],
  );

  return (
    <>
      <MoreActionsDropdown
        menu={_menu}
        hideHoverBorderRadius={hideHoverBorderRadius}
      />
      <RearrangeTasks
        visible={showRearrangeModal}
        currentTaskId={taskId}
        onClose={onClose}
      />
    </>
  );
};
